import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
    onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
        };
    }

    static getDerivedStateFromError(error: Error): State {
        return {
            hasError: true,
            error,
            errorInfo: null,
        };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        this.setState({
            error,
            errorInfo,
        });

        // Always log errors to console (production and development)
        // This helps diagnose production-only crashes
        const errorReport = {
            name: error.name,
            message: error.message,
            stack: error.stack,
            componentStack: errorInfo?.componentStack,
            route: typeof window !== 'undefined' ? window.location.pathname : 'unknown',
            timestamp: new Date().toISOString(),
            mode: import.meta.env.MODE,
        };

        console.error('[ErrorBoundary] React component error:', errorReport);
        console.error('[ErrorBoundary] Error object:', error);
        console.error('[ErrorBoundary] Component stack:', errorInfo?.componentStack);

        // Call custom error handler if provided
        this.props.onError?.(error, errorInfo);
    }

    handleRetry = () => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
        });

        // Trigger a re-render of child components
        this.forceUpdate();
    };

    handleGoHome = () => {
        // Use React Router navigation if available, otherwise fallback to window.location
        if (typeof window !== 'undefined' && window.history) {
            window.history.pushState({}, '', '/');
            window.dispatchEvent(new PopStateEvent('popstate'));
        } else {
            window.location.href = '/';
        }
    };

    render() {
        if (this.state.hasError) {
            // Custom fallback UI
            if (this.props.fallback) {
                return this.props.fallback;
            }

            // Default error UI
            return (
                <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
                    <Card className="w-full max-w-md">
                        <CardHeader className="text-center">
                            <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
                                <AlertTriangle className="w-6 h-6 text-red-600" />
                            </div>
                            <CardTitle className="text-xl text-gray-900">
                                Something went wrong
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <p className="text-gray-600 text-center">
                                We're sorry, but something unexpected happened. Please try again or
                                go back to the home page.
                            </p>

                            {import.meta.env.MODE === 'development' && this.state.error && (
                                <details className="mt-4 p-3 bg-gray-100 rounded-md">
                                    <summary className="cursor-pointer text-sm font-medium text-gray-700">
                                        Error Details (Development)
                                    </summary>
                                    <pre className="mt-2 text-xs text-gray-600 overflow-auto">
                                        {this.state.error.toString()}
                                        {this.state.errorInfo?.componentStack}
                                    </pre>
                                </details>
                            )}

                            <div className="flex flex-col sm:flex-row gap-2">
                                <Button
                                    onClick={this.handleRetry}
                                    className="flex-1"
                                    variant="outline"
                                >
                                    <RefreshCw className="w-4 h-4 mr-2" />
                                    Try Again
                                </Button>
                                <Button onClick={this.handleGoHome} className="flex-1">
                                    <Home className="w-4 h-4 mr-2" />
                                    Go Home
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            );
        }

        return this.props.children;
    }
}

// Hook for functional components to trigger error boundary
export const useErrorHandler = () => {
    const throwError = (error: Error) => {
        throw error;
    };

    return { throwError };
};

// Higher-order component for easier error boundary wrapping
export const withErrorBoundary = <P extends object>(
    Component: React.ComponentType<P>,
    errorBoundaryProps?: Omit<Props, 'children'>
) => {
    const WrappedComponent = (props: P) => (
        <ErrorBoundary {...errorBoundaryProps}>
            <Component {...props} />
        </ErrorBoundary>
    );

    WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;

    return WrappedComponent;
};
